(() => {
  'use strict';

  const SELECTOR = '[data-card-sender]';

  const debounce = (fn, wait = 100) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    };
  };

  const addBusinessDays = (date, days) => {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const weekday = result.getDay();
      if (weekday !== 0 && weekday !== 6) added += 1;
    }
    return result;
  };

  const toDateValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDate = (value) => {
    if (!value) return '—';
    return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
      .format(new Date(`${value}T12:00:00`));
  };

  const unitPriceFor = (quantity) => {
    if (quantity >= 25) return 3.99;
    if (quantity >= 10) return 4.49;
    if (quantity >= 5) return 4.99;
    if (quantity >= 3) return 5.49;
    return 5.99;
  };

  const formatCurrency = (value) => new Intl.NumberFormat('de-DE', {
    style: 'currency', currency: 'EUR'
  }).format(value);

  class CardSender {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-cs-form]');
      this.recipientsNode = root.querySelector('[data-cs-recipients]');
      this.template = root.querySelector('[data-cs-recipient-template]');
      this.maxCards = Math.max(1, Number(root.dataset.maxCards) || 50);
      this.leadDays = Math.max(0, Number(root.dataset.leadDays) || 3);
      this.variantId = String(root.dataset.variantId || '').trim();
      this.state = { quantity: 1, recipients: [], currentStep: 1 };
      this.previewUpdate = debounce((slip) => this.updatePreview(slip), 100);
      this.bindEvents();
      this.setQuantity(1);
      this.setMinimumDates();
      this.observeSteps();
    }

    bindEvents() {
      this.root.querySelector('[data-cs-start]').addEventListener('click', () => {
        this.root.querySelector('[data-cs-journey]').scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      this.root.querySelectorAll('input[type="radio"][name^="cs-quantity-"]').forEach((input) => {
        input.addEventListener('change', () => this.handleQuantityChoice(input));
      });

      this.root.querySelector('.cs-quantity__grid').addEventListener('click', (event) => {
        const label = event.target.closest('.cs-quantity__grid label');
        if (!label) return;
        const input = label.querySelector('input[type="radio"]') || this.root.querySelector(`#${CSS.escape(label.htmlFor)}`);
        if (!input) return;
        event.preventDefault();
        this.root.querySelectorAll('input[type="radio"][name^="cs-quantity-"]').forEach((radio) => {
          radio.checked = radio === input;
        });
        this.syncQuantitySelection();
        this.handleQuantityChoice(input);
      });

      const custom = this.root.querySelector('[data-cs-custom-quantity]');
      custom.addEventListener('input', () => this.handleCustomQuantityInput(custom));
      custom.addEventListener('blur', () => this.commitCustomQuantity(custom));
      custom.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.commitCustomQuantity(custom);
        }
      });
      this.root.querySelector('[data-cs-decrease]').addEventListener('click', () => this.changeCustomQuantity(-1));
      this.root.querySelector('[data-cs-increase]').addEventListener('click', () => this.changeCustomQuantity(1));
      this.root.querySelector('[data-cs-add-card]').addEventListener('click', () => this.setQuantity(this.state.quantity + 1, true));

      this.root.querySelectorAll('[data-cs-next]').forEach((button) => {
        button.addEventListener('click', () => this.goToStep(Number(button.dataset.csNext)));
      });
      this.root.querySelectorAll('[data-cs-go-step]').forEach((button) => {
        button.addEventListener('click', () => this.goToStep(Number(button.dataset.csGoStep), false));
      });

      this.recipientsNode.addEventListener('click', (event) => this.handleRecipientClick(event));
      this.recipientsNode.addEventListener('input', (event) => this.handleRecipientInput(event));
      this.recipientsNode.addEventListener('change', (event) => this.handleRecipientInput(event));
      this.form.addEventListener('submit', (event) => this.submit(event));
    }

    clampQuantity(value) {
      return Math.min(this.maxCards, Math.max(1, Math.round(Number(value) || 1)));
    }

    handleQuantityChoice(input) {
      const stepper = this.root.querySelector('[data-cs-stepper]');
      const isCustom = input.value === 'custom';
      stepper.hidden = !isCustom;
      const quantity = isCustom
        ? this.root.querySelector('[data-cs-custom-quantity]').value
        : input.value;
      this.setQuantity(this.clampQuantity(quantity), isCustom);
      if (isCustom) this.root.querySelector('[data-cs-custom-quantity]').focus();
    }

    handleCustomQuantityInput(input) {
      const rawValue = input.value.trim();
      const customRadio = this.root.querySelector('input[type="radio"][value="custom"]');
      if (customRadio) customRadio.checked = true;
      this.root.querySelector('[data-cs-stepper]').hidden = false;
      this.syncQuantitySelection();

      // Beim Tippen darf das Feld kurz leer sein, sonst springt es sofort zurück auf 1.
      if (rawValue === '') return;

      const parsed = Number(rawValue);
      if (!Number.isFinite(parsed)) return;

      this.setQuantity(this.clampQuantity(parsed), true);
    }

    commitCustomQuantity(input) {
      const quantity = this.clampQuantity(input.value);
      input.value = quantity;
      this.setQuantity(quantity, true);
    }

    changeCustomQuantity(delta) {
      const input = this.root.querySelector('[data-cs-custom-quantity]');
      const next = this.clampQuantity(Number(input.value) + delta);
      input.value = next;
      this.setQuantity(next, true);
    }

    setQuantity(value, forceCustom = false) {
      const quantity = this.clampQuantity(value);
      this.state.quantity = quantity;

      while (this.recipientsNode.children.length < quantity) this.addRecipient();
      while (this.recipientsNode.children.length > quantity) this.recipientsNode.lastElementChild.remove();

      if (forceCustom || ![1, 3, 5, 10].includes(quantity)) {
        const customRadio = this.root.querySelector('input[type="radio"][value="custom"]');
        customRadio.checked = true;
        this.root.querySelector('[data-cs-stepper]').hidden = false;
        this.root.querySelector('[data-cs-custom-quantity]').value = quantity;
      } else {
        const customRadio = this.root.querySelector('input[type="radio"][value="custom"]');
        const presetRadio = this.root.querySelector(`input[type="radio"][value="${quantity}"]`);
        if (customRadio) customRadio.checked = false;
        if (presetRadio) presetRadio.checked = true;
        this.root.querySelector('[data-cs-stepper]').hidden = true;
      }

      this.syncQuantitySelection();
      this.renumberRecipients();
      this.root.querySelector('[data-cs-add-card]').disabled = quantity >= this.maxCards;
      const unitPrice = unitPriceFor(quantity);
      const cardLabel = quantity === 1 ? 'Karte' : 'Karten';
      this.root.querySelector('[data-cs-quantity-hint]').textContent =
        `${quantity} ${cardLabel} · ${formatCurrency(unitPrice)} je Karte · Gesamt ${formatCurrency(quantity * unitPrice)}`;
      this.syncState();
    }

    syncQuantitySelection() {
      this.root.querySelectorAll('input[type="radio"][name^="cs-quantity-"]').forEach((radio) => {
        const label = radio.closest('label') || this.root.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
        if (label) label.classList.toggle('is-selected', radio.checked);
      });
    }

    addRecipient() {
      const index = this.recipientsNode.children.length;
      const html = this.template.innerHTML
        .replaceAll('__INDEX__', String(index))
        .replaceAll('__NUMBER__', String(index + 1));
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html.trim();
      const slip = wrapper.firstElementChild;
      this.recipientsNode.appendChild(slip);
      this.applyMinimumDate(slip);
      this.updateDesignSummary(slip);
      if (index > 0) this.setSlipOpen(slip, false);
    }

    renumberRecipients() {
      [...this.recipientsNode.children].forEach((slip, index) => {
        slip.dataset.csIndex = String(index);
        slip.querySelector('.cs-slip__index').textContent = `Karte ${index + 1}`;
        slip.querySelector('label[for*="CsMessage-"]').textContent = `Persönliche Nachricht für Karte ${index + 1}`;
      });
    }

    handleRecipientClick(event) {
      const toggle = event.target.closest('[data-cs-toggle]');
      if (toggle) {
        this.setSlipOpen(toggle.closest('[data-cs-slip]'), toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }
      const remove = event.target.closest('[data-cs-remove]');
      if (remove) {
        if (this.state.quantity <= 1) return;
        remove.closest('[data-cs-slip]').remove();
        this.state.quantity -= 1;
        this.setQuantity(this.state.quantity, true);
      }
    }

    setSlipOpen(slip, open) {
      const toggle = slip.querySelector('[data-cs-toggle]');
      toggle.setAttribute('aria-expanded', String(open));
      slip.querySelector('[data-cs-slip-body]').hidden = !open;
      if (open) this.updatePreview(slip);
    }

    handleRecipientInput(event) {
      const field = event.target.closest('[data-cs-field]');
      if (!field) return;
      const slip = field.closest('[data-cs-slip]');
      if (field.dataset.csField === 'name') {
        slip.querySelector('[data-cs-summary-name]').textContent = field.value.trim() || 'Noch unbenannt';
      }
      if (field.dataset.csField === 'date') {
        slip.querySelector('[data-cs-summary-date]').textContent = formatDate(field.value);
      }
      if (field.dataset.csField === 'design') {
        this.updateDesignSummary(slip);
      }
      if (field.dataset.csField === 'message') {
        const counter = slip.querySelector('[data-cs-counter]');
        counter.textContent = `${field.value.length}/300`;
        counter.classList.toggle('is-near-limit', field.value.length >= 240);
      }
      this.previewUpdate(slip);
      this.syncState();
    }

    updateDesignSummary(slip) {
      const design = this.selectedDesign(slip);
      const summary = slip.querySelector('[data-cs-summary-design]');
      if (summary) summary.textContent = design || 'Design wählen';
    }

    selectedDesign(slip) {
      const select = slip.querySelector('[data-cs-field="design"]');
      return select?.selectedOptions?.[0]?.textContent?.trim() || select?.value?.trim() || '';
    }

    updatePreview(slip) {
      if (!slip) return;
      const name = slip.querySelector('[data-cs-field="name"]').value.trim();
      const message = slip.querySelector('[data-cs-field="message"]').value.trim();
      const design = this.selectedDesign(slip);
      this.root.querySelector('[data-cs-preview-to]').textContent = name ? `Für ${name}` : 'Für einen lieben Menschen';
      this.root.querySelector('[data-cs-preview-design]').textContent = design ? `Design: ${design}` : 'Design: noch nicht gewählt';
      this.root.querySelector('[data-cs-preview-message]').textContent = message || 'Deine persönliche Nachricht erscheint hier …';
      this.root.querySelector('.cs-preview__note').textContent = `Live-Vorschau · Karte ${Number(slip.dataset.csIndex) + 1}`;
    }

    setMinimumDates() {
      this.recipientsNode.querySelectorAll('[data-cs-slip]').forEach((slip) => this.applyMinimumDate(slip));
    }

    applyMinimumDate(slip) {
      const dateInput = slip.querySelector('[data-cs-field="date"]');
      dateInput.min = toDateValue(addBusinessDays(new Date(), this.leadDays));
    }

    observeSteps() {
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) this.setCurrentStep(Number(visible.target.dataset.csStep));
      }, { rootMargin: '-22% 0px -55%', threshold: [0.1, 0.35, 0.65] });
      this.root.querySelectorAll('[data-cs-step]').forEach((step) => observer.observe(step));
    }

    setCurrentStep(step) {
      this.state.currentStep = step;
      const names = ['Menge', 'Empfänger', 'Absender'];
      this.root.querySelectorAll('[data-cs-progress-item]').forEach((item) => {
        const itemStep = Number(item.dataset.csProgressItem);
        item.classList.toggle('is-active', itemStep === step);
        item.classList.toggle('is-complete', itemStep < step);
        const button = item.querySelector('button');
        if (itemStep === step) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      this.root.querySelector('[data-cs-progress-bar]').style.width = `${((step - 1) / 2) * 100}%`;
      this.root.querySelector('[data-cs-step-live]').textContent = `Schritt ${step} von 3: ${names[step - 1]}`;
    }

    goToStep(step, validate = true) {
      if (validate && step > this.state.currentStep && !this.validateThrough(step - 1)) return;
      const target = this.root.querySelector(`[data-cs-step="${step}"]`);
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.setCurrentStep(step);
    }

    validateThrough(step) {
      const scope = this.root.querySelector(`[data-cs-step="${step}"]`);
      const invalid = scope.querySelector(':invalid');
      if (!invalid) return true;
      const slip = invalid.closest('[data-cs-slip]');
      if (slip) this.setSlipOpen(slip, true);
      invalid.focus();
      invalid.reportValidity();
      return false;
    }

    syncState() {
      this.state.recipients = [...this.recipientsNode.querySelectorAll('[data-cs-slip]')].map((slip) => {
        const value = (name) => slip.querySelector(`[data-cs-field="${name}"]`).value.trim();
        return {
          design: value('design'),
          name: value('name'), company: value('company'), street: value('street'),
          zip: value('zip'), city: value('city'), country: value('country'),
          date: value('date'), message: value('message')
        };
      });
    }

    buildItems() {
      this.syncState();
      const senderName = this.form.elements.sender_name.value.trim();
      const senderEmail = this.form.elements.sender_email.value.trim();
      return this.state.recipients.map((recipient, index) => {
        const number = index + 1;
        const address = [recipient.company, recipient.street, `${recipient.zip} ${recipient.city}`, recipient.country]
          .filter(Boolean).join(', ');
        return {
          id: Number(this.variantId),
          quantity: 1,
          properties: {
            [`Karte ${number} – Design`]: recipient.design,
            [`Karte ${number} – Empfänger`]: recipient.name,
            [`Karte ${number} – Adresse`]: address,
            [`Karte ${number} – Versand am`]: recipient.date,
            [`Karte ${number} – Nachricht`]: recipient.message,
            'Absender – Name': senderName,
            'Absender – E-Mail': senderEmail
          }
        };
      });
    }

    async submit(event) {
      event.preventDefault();
      this.showMessage('');
      if (!this.form.checkValidity()) {
        const invalid = this.form.querySelector(':invalid');
        const step = invalid.closest('[data-cs-step]');
        const slip = invalid.closest('[data-cs-slip]');
        if (slip) this.setSlipOpen(slip, true);
        if (step) this.setCurrentStep(Number(step.dataset.csStep));
        invalid.focus();
        invalid.reportValidity();
        this.showMessage('Bitte prüfe die markierten Angaben. Fast geschafft.');
        return;
      }
      if (!/^\d+$/.test(this.variantId)) {
        this.showMessage('Die Karten-Variante ist noch nicht eingerichtet. Bitte kontaktiere uns kurz.');
        return;
      }

      const button = this.root.querySelector('[data-cs-submit]');
      this.setLoading(button, true);
      try {
        const clearResponse = await fetch('/cart/clear.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
        });
        if (!clearResponse.ok) throw new Error('Der Warenkorb konnte nicht vorbereitet werden.');

        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: this.buildItems() })
        });
        if (!addResponse.ok) {
          const payload = await addResponse.json().catch(() => ({}));
          throw new Error(payload.description || 'Die Karten konnten nicht in den Warenkorb gelegt werden.');
        }

        await this.celebrate();
        window.location.assign('/checkout');
      } catch (error) {
        this.showMessage(error.message || 'Etwas ist schiefgelaufen. Bitte versuche es noch einmal.');
        this.setLoading(button, false);
      }
    }

    setLoading(button, loading) {
      button.disabled = loading;
      button.setAttribute('aria-busy', String(loading));
      button.querySelector('[data-cs-submit-label]').hidden = loading;
      button.querySelector('[data-cs-loading]').hidden = !loading;
    }

    showMessage(message) {
      this.root.querySelector('[data-cs-message]').textContent = message;
    }

    celebrate() {
      const overlay = this.root.querySelector('[data-cs-success]');
      overlay.hidden = false;
      overlay.setAttribute('aria-hidden', 'false');
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return new Promise((resolve) => window.setTimeout(resolve, 250));
      }
      return this.runConfetti(overlay.querySelector('[data-cs-confetti]'));
    }

    runConfetti(canvas) {
      const context = canvas.getContext('2d');
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const width = window.innerWidth;
      const height = window.innerHeight;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      context.scale(ratio, ratio);
      const colors = ['#c7572e', '#d9aa7f', '#7c8a68', '#1a1614', '#f2d9b7'];
      const particles = Array.from({ length: 60 }, () => ({
        x: Math.random() * width, y: -20 - Math.random() * height * .35,
        vx: (Math.random() - .5) * 3, vy: 3 + Math.random() * 5,
        size: 4 + Math.random() * 7, spin: Math.random() * Math.PI,
        color: colors[Math.floor(Math.random() * colors.length)]
      }));
      const started = performance.now();

      return new Promise((resolve) => {
        const frame = (now) => {
          context.clearRect(0, 0, width, height);
          particles.forEach((particle) => {
            particle.x += particle.vx;
            particle.y += particle.vy;
            particle.spin += .12;
            context.save();
            context.translate(particle.x, particle.y);
            context.rotate(particle.spin);
            context.fillStyle = particle.color;
            context.fillRect(-particle.size / 2, -particle.size / 3, particle.size, particle.size * .65);
            context.restore();
          });
          if (now - started < 600) window.requestAnimationFrame(frame);
          else {
            context.clearRect(0, 0, width, height);
            resolve();
          }
        };
        window.requestAnimationFrame(frame);
      });
    }
  }

  const init = () => {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.csInitialized) return;
      root.dataset.csInitialized = 'true';
      new CardSender(root);
    });
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState !== 'loading') init();
})();